import { getNationalThermalChart } from "$/api/market-monitoring/fundamentals.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import ComposedAreaGraph from "$/lib/components/graphs/ComposedAreaGraph";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { getDate } from "$/lib/utils/functions/date.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { endOfYear, startOfYear } from "date-fns";
import { useEffect, useMemo, useState } from "react";

const COLORS: Record<string, string> = {
  extreme: "#ececec",
  std: "#c7c7c7",
  normale: "#000",
};

const WORDING: Record<string, string> = {
  extreme: "Temp. extrêmes",
  std: "Ecart type",
  normale: "Temp. normales",
  itn_display: "ITN",
};

const NationalThermalChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("CURRENT_YEAR");

  const startOfCurrentYear = startOfYear(new Date());
  const endOfCurrentYear = endOfYear(new Date());

  const [dateRange, setDateRange] = useState<[Date, Date]>([
    startOfCurrentYear,
    endOfCurrentYear,
  ]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-national-thermal-chart", dateRange],
    queryFn: () =>
      getNationalThermalChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const keys = useMemo(() => getArrayKeys(data ?? []), [data]);
  const result = useMemo(() => getMinMax(data ?? [], keys), [data, keys]);

  const displayedKeys = useMemo(
    () => keys.filter((key) => !key.includes("itn")),
    [keys],
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  const stops = useMemo(() => {
    const result: [string, string, string][] = [];

    if (!Array.isArray(data) || data.length === 0) return result;

    let segmentStartIndex: number | null = null;
    let prevAboveNormale: boolean | null = null;

    for (let i = 0; i < data.length; i++) {
      const point = data[i];

      if (
        !point ||
        typeof point.itn_value !== "number" ||
        typeof point.normale !== "number"
      ) {
        continue;
      }

      const currentAboveNormale = point.itn_value > point.normale;

      if (segmentStartIndex === null) {
        segmentStartIndex = i;
        prevAboveNormale = currentAboveNormale;
        continue;
      }

      if (currentAboveNormale !== prevAboveNormale) {
        const startPercent = (segmentStartIndex / data.length) * 100;
        const endPercent = (i / data.length) * 100;
        const color = prevAboveNormale ? "#c07c81" : "#7496CB";

        result.push([`${startPercent}%`, `${endPercent}%`, color]);

        segmentStartIndex = i;
        prevAboveNormale = currentAboveNormale;
      }
    }

    if (segmentStartIndex !== null && prevAboveNormale !== null) {
      const startPercent = (segmentStartIndex / data.length) * 100;
      const endPercent = 100;
      const color = prevAboveNormale ? "#c07c81" : "#7496CB";
      result.push([`${startPercent}%`, `${endPercent}%`, color]);
    }

    return result;
  }, [data]);

  return (
    <DisplayBox
      title="Indicateurs thermiques nationaux"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      Icon={LineChartIcon}
      theme="orange"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="!h-full !min-h-[500px] !py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <ComposedAreaGraph
            data={data ?? []}
            xDataKey="date"
            areaKeys={["extreme", "std"]}
            hideKeys={["itn", "itn_value"]}
            notVisibleKeys={["itn_display"]}
            lineKey="normale"
            areaGradientKey="itn"
            colors={COLORS}
            unit=" °C"
            domain={[result.min.value, result.max.value]}
            theme="orange"
            hasNumberFormatter
            leftMargin={-35}
            displayNames={WORDING}
            stops={stops}
            heightPercent={70}
          />
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="orange"
          />

          <GraphFilter
            title="Légende"
            data={displayedKeys.map((key) => ({
              name: WORDING[key] ?? key,
              color: COLORS[key],
            }))}
            handleOnChange={() => {}}
          />

          <GraphFilter
            title="ITN"
            data={[
              {
                name: "Temp. < normale",
                color: " #7496CB",
              },
              {
                name: "Temp. > normale",
                color: "#c07c81",
              },
            ]}
            handleOnChange={() => {}}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default NationalThermalChart;
